import { useState, useCallback } from 'react';

const STORAGE_KEY = 'butlr-hidden-spaces';

function loadHiddenIds(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return new Set();

    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      console.warn(
        `[useHiddenSpaces] Invalid localStorage data: expected array, got ${typeof parsed}`,
      );
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* no-op */
      }
      return new Set();
    }
    return new Set(parsed.filter((item): item is string => typeof item === 'string'));
  } catch (err) {
    if (!(err instanceof DOMException)) {
      console.warn('[useHiddenSpaces] Failed to load hidden spaces:', err);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* no-op */
      }
    }
    return new Set();
  }
}

function saveHiddenIds(ids: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch (err) {
    if (!(err instanceof DOMException)) {
      console.warn('[useHiddenSpaces] Failed to save hidden spaces:', err);
    }
  }
}

export function useHiddenSpaces() {
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(loadHiddenIds);

  const toggleHidden = useCallback((id: string) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      saveHiddenIds(next);
      return next;
    });
  }, []);

  const setManyHidden = useCallback((ids: string[], hidden: boolean) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (hidden ? next.add(id) : next.delete(id)));
      saveHiddenIds(next);
      return next;
    });
  }, []);

  const isHidden = useCallback((id: string) => hiddenIds.has(id), [hiddenIds]);

  return { hiddenIds, toggleHidden, setManyHidden, isHidden };
}
